import React from 'react';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const cards = [
  {
    icon: 'ri-shield-cross-line',
    title: 'Falsa sensação de segurança',
    description:
      'Empresas pagam seguro regularmente, mas não estão protegidas no que realmente importa.',
  },
  {
    icon: 'ri-file-damage-line',
    title: 'Coberturas mal estruturadas',
    description:
      'Riscos importantes ficam descobertos por falta de análise técnica.',
  },
  {
    icon: 'ri-file-unknow-line',
    title: 'Cláusulas ocultas e exclusões',
    description:
      'Problemas só aparecem no momento do sinistro, quando já não há solução.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Prejuízos silenciosos',
    description:
      'O valor investido no seguro pode se transformar em perda total.',
  },
];

export default function FalsaProtecaoEmpresarial() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(cards.length);
  const { ref: quoteRef, isVisible: quoteVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-red-50/25 to-zinc-100 py-12 sm:py-16 lg:py-28 overflow-hidden">
      {/* Background subtle grid */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Soft accent glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-100/60 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-zinc-200/60 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[620px]">

          {/* ── Coluna Esquerda — Conteúdo ── */}
          <div className="space-y-6 sm:space-y-8">

            {/* Badge */}
            <div
              ref={headerRef as React.RefObject<HTMLDivElement>}
              className={`transition-all duration-700 ease-out ${
                headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 sm:px-5 py-2">
                <i className="ri-error-warning-line text-red-600 text-base sm:text-lg" />
                <span className="text-red-700 font-bold text-xs sm:text-sm tracking-[0.15em] uppercase">
                  Atenção
                </span>
              </div>

              {/* Título */}
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 leading-[1.15] mt-5 sm:mt-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Sem assessoria especializada, sua empresa pode estar pagando caro por uma{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">
                  falsa proteção
                </span>
              </h2>

              {/* Subtítulo */}
              <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed mt-4 sm:mt-5">
                A maioria das empresas acredita que está protegida — até o momento do sinistro provar o contrário.
              </p>
            </div>

            {/* Cards Grid */}
            <div
              ref={gridRef as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
            >
              {cards.map((card, index) => (
                <div
                  key={index}
                  className={`bg-white border border-slate-200 rounded-xl p-4 sm:p-5 hover:border-red-300 hover:bg-red-50/20 group transition-all duration-700 ease-out ${
                    visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center group-hover:border-red-400 group-hover:bg-red-100 transition-colors">
                      <i className={`${card.icon} text-red-500 text-lg sm:text-xl`} />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <h3 className="text-slate-900 font-bold text-sm sm:text-base mb-1 leading-snug">
                        {card.title}
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Frase de Destaque */}
            <div
              ref={quoteRef as React.RefObject<HTMLDivElement>}
              className={`relative bg-slate-900 rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-700 ease-out ${
                quoteVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
              }`}
            >
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-14 sm:h-16 bg-gradient-to-b from-red-500 to-orange-500 rounded-r" />
              <div className="flex items-start gap-2.5 sm:gap-3 pl-3">
                <i className="ri-double-quotes-l text-red-400/25 text-2xl sm:text-3xl flex-shrink-0" />
                <p className="text-white text-base sm:text-lg lg:text-xl font-bold leading-relaxed">
                  O maior risco não é não ter seguro. É ter um seguro que não protege.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div
              ref={ctaRef as React.RefObject<HTMLDivElement>}
              className={`pt-2 sm:pt-4 transition-all duration-700 ease-out ${
                ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <a
                href="#analise-gratuita"
                className="group flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl transition-all duration-300 w-full sm:w-auto text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line text-xl sm:text-2xl shrink-0" />
                <span className="text-sm sm:text-base lg:text-lg leading-tight">
                  Quero uma análise estratégica da minha empresa
                </span>
                <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform shrink-0" />
              </a>
              <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 italic flex items-center justify-center sm:justify-start gap-2">
                <i className="ri-shield-star-line text-emerald-500" />
                Diagnóstico completo sem compromisso.
              </p>
            </div>
          </div>

          {/* ── Coluna Direita — Imagem ── */}
          <div className="relative h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[620px] mt-4 lg:mt-0">
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">

              {/* Imagem Principal */}
              <img
                src="https://readdy.ai/api/search-image?query=serious%20corporate%20business%20executive%20sitting%20at%20boardroom%20table%20looking%20worried%20and%20stressed%20while%20reviewing%20insurance%20policy%20documents%20and%20contracts%20with%20highlighted%20exclusion%20clauses%20dark%20shadows%20visible%20in%20modern%20office%20environment%20muted%20neutral%20background%20professional%20photography%20cinematic%20lighting%20premium%20quality%20realistic%20atmosphere%20of%20financial%20concern%20and%20corporate%20risk&width=800&height=900&seq=falsa-protecao-empresarial-main&orientation=portrait"
                alt="Executivo empresarial preocupado revisando apólice de seguro com cláusulas problemáticas"
                className="w-full h-full object-cover object-top"
              />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent" />

              {/* Badge superior direito — estatística de impacto */}
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-red-600 text-white rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-red-500/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                  <i className="ri-shield-cross-fill text-base sm:text-lg" />
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-90">
                    Falsa proteção
                  </span>
                </div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold leading-none">73%</div>
                <div className="text-[10px] sm:text-xs font-medium opacity-90 whitespace-nowrap">
                  das empresas sem análise técnica
                </div>
              </div>

              {/* Badge lateral esquerdo — aviso */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 sm:left-6 bg-white rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 max-w-[150px] sm:max-w-none">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-alert-fill text-orange-500 text-xl sm:text-2xl shrink-0" />
                  </div>
                  <div>
                    <div className="text-slate-900 font-bold text-sm sm:text-base leading-none">Sinistro</div>
                    <div className="text-slate-600 text-[10px] sm:text-xs font-medium whitespace-nowrap">
                      sem cobertura real
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Flutuante Inferior */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center">
                    <i className="ri-error-warning-line text-white text-xl sm:text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-base sm:text-lg mb-0.5 sm:mb-1">
                      Cobertura ilusória
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      As falhas só se revelam quando o prejuízo já é irreversível
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* ── Fim Coluna Direita ── */}

        </div>
      </div>
    </section>
  );
}
